import React, { useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { UserProvider, useUser } from './contexts/UserContext';

import NeuralGlassHomepage from './components/NeuralGlassHomepage';
import NeuralHomepage from './components/NeuralHomepage';
import DesktopPage from './components/DesktopPage';
import InterviewAssistant from './components/InterviewPage';
import InterviewAssistantLanding from './components/InterviewAssistantLanding';
import CoverLetter from './components/CoverLetterPage';
import CoverLetterLanding from './components/CoverLetterLanding';
import InterviewQuestions from './components/InterviewQuestionsPage';
import InterviewQuestionsLanding from './components/InterviewQuestionsLanding';
import BuildResume from './components/ResumeBuilderPage';
import ResumeBuilderLanding from './components/ResumeBuilderLanding';
import ResumeStudioPage from './components/ResumeStudioPage';
import CareerPathAdvisor from './components/CareerPathAdvisor';
import ATSCheckPage from './components/ATSCheckPage';
import ATSCheckLanding from './components/ATSCheckLanding';
import Dashboard from './components/Dashboard';
import PaymentSuccess from './components/PaymentSuccess';
import MockInterviewPage from './components/MockInterviewPage';
import MockInterviewResults from './components/MockInterviewResults';
import MockInterviewLanding from './components/MockInterviewLanding';
import ProfilePage from './components/ProfilePage';
import LinkedInOptimizer from './components/LinkedInOptimizerPage';
import LinkedInOptimizerLanding from './components/LinkedInOptimizerLanding';
import LinkedInStudioPage from './components/LinkedInStudioPage';
import AutoJobApply from './components/AutoJobApply';
import AutoJobApplyLanding from './components/AutoJobApplyLanding';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import About from './components/About';
import FloatingOverlay from './components/FloatingOverlay';
import CertificationPage from './components/CertificationPage';
import CertificationLanding from './components/CertificationLanding';
import PortfolioPage from './components/PortfolioPage';
import PortfolioBuilderLanding from './components/PortfolioBuilderLanding';
import PublicPortfolioPage from './components/PublicPortfolioPage';
import BlogIndex from './components/BlogIndex';
import BlogPost from './components/BlogPost';
import BlogEditor from './components/BlogEditor';
import Onboarding from './components/Onboarding/Onboarding';
import { ToastProvider } from './components/Toast/ToastProvider';
import ElectronShell from './electron/ElectronShell';
import TeraChat from './components/TeraChat';

import './App.css';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Gates the app on first run. If a logged-in user has no profile yet (or
// is_complete = 0), redirects them to /onboarding the moment they try to
// touch any feature page. Public pages and /onboarding itself are exempt.
const PUBLIC_PATHS = new Set([
  '/', '/privacy-policy', '/terms-of-service', '/about',
  '/floating', '/payment-success', '/onboarding',
]);
const PUBLIC_PREFIXES = ['/p/']; // public portfolio
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const OnboardingGate = () => {
  const { user, loading } = useUser();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || !user) return;
    if (PUBLIC_PATHS.has(pathname) || PUBLIC_PREFIXES.some(p => pathname.startsWith(p))) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/profile/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        // The /profile/status endpoint returns { profileComplete }, NOT
        // { isComplete }. Reading the wrong field name caused an infinite
        // redirect loop where a finished user kept getting bounced back here.
        if (!data.profileComplete && pathname !== '/onboarding') {
          navigate('/onboarding', { replace: true });
        }
      } catch { /* network errors → don't block; fail open */ }
    })();
    return () => { cancelled = true; };
  }, [user, loading, pathname, navigate]);

  // Pure route-level side-effect guard. Renders nothing.
  return null;
};

const isElectron = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isElectronUA = userAgent.includes('electron');
  const hasProcess = window.process && window.process.type;
  return isElectronUA || !!hasProcess;
};

const RouterComponent = isElectron() ? HashRouter : BrowserRouter;
const IS_ELECTRON = isElectron();

// Routes where the floating Tera chat would compete with the workspace
// or modal (live interview, mock-interview session, billing checkout, etc.)
// Add more here as needed — match against the start of the pathname.
const TERA_HIDDEN_PREFIXES = [
  '/interview-session',
  '/mock-interview-session',
  '/auto-apply-session',
];

// Hides the floating Tera chat on routes where it would crowd the UI.
const TeraChatGate = () => {
  const { pathname } = useLocation();
  const hidden = TERA_HIDDEN_PREFIXES.some(p => pathname.startsWith(p));
  if (hidden) return null;
  return <TeraChat />;
};

// All app routes in one place so we can render them inside ElectronShell
// (desktop) or directly under <Routes> (web) without duplicating the list.
const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<NeuralHomepage />} />
    <Route path="/home-legacy" element={<NeuralGlassHomepage />} />
    <Route path="/onboarding" element={<Onboarding />} />
    <Route path="/profile" element={<ProfilePage />} />
    <Route path="/InterviewAssistant" element={<InterviewAssistantLanding />} />
    <Route path="/desktop" element={<DesktopPage />} />
    <Route path="/interview-session" element={<InterviewAssistant embedded={IS_ELECTRON} />} />
    <Route path="/BuildResume" element={<Navigate to="/resume-builder" replace />} />
    <Route path="/resume-builder" element={<ResumeBuilderLanding />} />
    <Route path="/resume-studio" element={<ResumeStudioPage />} />
    <Route path="/resume-builder-legacy" element={<BuildResume />} />
    <Route path="/CoverLetter" element={<CoverLetterLanding />} />
    <Route path="/cover-letter-session" element={<CoverLetter />} />
    <Route path="/InterviewQuestions" element={<InterviewQuestionsLanding />} />
    <Route path="/interview-questions-session" element={<InterviewQuestions />} />
    <Route path="/CareerPathAdvisor" element={<CareerPathAdvisor />} />
    <Route path="/ATSCheck" element={<ATSCheckLanding />} />
    <Route path="/ats-check-session" element={<ATSCheckPage />} />
    <Route path="/Dashboard" element={<Dashboard />} />
    <Route path="/payment-success" element={<PaymentSuccess />} />
    <Route path="/mockInterview" element={<MockInterviewLanding />} />
    <Route path="/mock-interview-session" element={<MockInterviewPage />} />
    <Route path="/mock-interview-results" element={<MockInterviewResults />} />
    <Route path="/LinkedInOptimizer" element={<LinkedInOptimizerLanding />} />
    <Route path="/linkedin-studio" element={<LinkedInStudioPage />} />
    <Route path="/linkedin-optimizer-session" element={<LinkedInOptimizer />} />
    <Route path="/AutoJobApply" element={<AutoJobApplyLanding />} />
    <Route path="/auto-apply-session" element={<AutoJobApply />} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms-of-service" element={<TermsOfService />} />
    <Route path="/about" element={<About />} />
    <Route path="/floating" element={<FloatingOverlay />} />
    <Route path="/certification" element={<CertificationLanding />} />
    <Route path="/certification-session" element={<CertificationPage />} />
    <Route path="/portfolio" element={<PortfolioBuilderLanding />} />
    <Route path="/portfolio-session" element={<PortfolioPage />} />
    <Route path="/p/:slug" element={<PublicPortfolioPage />} />
    <Route path="/blog" element={<BlogIndex />} />
    <Route path="/blog/admin/new" element={<BlogEditor mode="new" />} />
    <Route path="/blog/admin/edit/:id" element={<BlogEditor mode="edit" />} />
    <Route path="/blog/:slug" element={<BlogPost />} />
  </Routes>
);

function App() {
  return (
    <UserProvider>
      <ToastProvider>
      <RouterComponent>
        <ScrollToTop />
        <OnboardingGate />
        <div className="App">
          {IS_ELECTRON ? (
            // Desktop layout — sidebar + active pane. ElectronShell decides
            // when to render the routed page full-bleed (live sessions) vs
            // its own pane content (feature task panes).
            <ElectronShell>
              <AppRoutes />
            </ElectronShell>
          ) : (
            <>
              <AppRoutes />
              {/* Floating AI chat agent — web only. Hidden inside the
                  Electron shell which has its own first-class panes,
                  AND on live interview / mock interview sessions where
                  the chat would crowd the workspace. */}
              <TeraChatGate />
            </>
          )}
        </div>
      </RouterComponent>
      </ToastProvider>
    </UserProvider>
  );
}

export default App;
